import { inject, Injectable, OnDestroy } from '@angular/core';
import { ModalService } from './modal.service';
import { SidebarService } from './sidebar.service';
import { SidebarChangedEvent } from '../utils/events/sidebar-changed-event';
import { SearchComponent } from '../components/sidebar/search/search.component';
import {
    AccessibilityModalComponent
} from '../components/bookviewer/pages/modal-container/accessibility-modal/accessibility-modal.component';
import { AssignmentsLink } from '../models/assignments-link';
import { CommunicationService } from './communication.service';
import { PageChangedEvent } from '../utils/events/page-changed-event';
import { ChapterContentComponent } from '../components/sidebar/chapter-content/chapter-content.component';
import { ChapterService } from './chapter.service';
import { Subscription } from 'rxjs';
import { QuickLink } from '../models/quick-link';
import { ModalableWhiteboardDrawing } from '../models/modalable-whiteboard-drawing';

@Injectable({
    providedIn: 'root'
})
export class AccessibilityService implements OnDestroy {
    private modalService = inject(ModalService);
    private sideBarService = inject(SidebarService);
    private communicationService = inject(CommunicationService);
    private chapterService = inject(ChapterService);

    private isModalOpen = false;
    private sideBarTriggerElement: HTMLElement|null = null;

    private subscriptions: Array<Subscription> = [];

    init(): void {
        window.addEventListener('keydown', (event: KeyboardEvent) => {
            this.handleKeyDown(event);
        });
        window.document.addEventListener('hidden.bs.modal', (event)=> {
            const modal = event.target as HTMLElement;
            if (modal.id === AccessibilityModalComponent.MODAL_ID) {
                this.isModalOpen = false;
            }
            if (modal.id === AssignmentsLink.MODAL_ID || modal.id === QuickLink.MODAL_ID) {
                this.returnFocusToPageContent();
                this.modalService.triggerElement = null;

                return;
            }
            if (this.modalService.triggerElement !== null) {
                this.modalService.triggerElement.focus();
                this.modalService.triggerElement = null;
            }
        });
        window.document.addEventListener('shown.bs.modal', (event) => {
            const modal = event.target as HTMLElement;

            if ((modal.id === AssignmentsLink.MODAL_ID ||
                modal.id === QuickLink.MODAL_ID ||
                modal.id === ModalableWhiteboardDrawing.MODAL_ID)
                && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();

                return;
            }

            this.focusInsideModal(modal);
        });
        this.subscriptions.push(this.sideBarService.sidebarEventFired$.subscribe((event: SidebarChangedEvent) => {
            if (event.openSidebar) {
                this.sideBarTriggerElement = document.activeElement as HTMLElement;
                this.focusSidebar();

                return;
            }

            if (event.closeSidebar) {
                if (this.sideBarTriggerElement) {
                    this.focusElementById('sidebarWrapper');
                    this.sideBarTriggerElement.focus();
                    this.sideBarTriggerElement = null;
                }
            }
        }));

        this.subscriptions.push(this.communicationService.pageTurnAnnounced$.subscribe(() => {
            this.returnFocusToPageContent();
        }));
        this.subscriptions.push(this.communicationService.viewModeChangeAnnounced$.subscribe(() => {
            const bookviewerContainer = document.getElementsByClassName('bookviewer-container')[0] as HTMLElement;
            if (bookviewerContainer) {
                bookviewerContainer.focus()
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    private handleKeyDown(event: KeyboardEvent): void {
        const activeElement = document.activeElement as HTMLElement;
        const isTyping = this.checkIsTyping(activeElement);

        // Shift + ? (Standard shortcut for help)
        // Check if key is '?' and not typing in text fields
        if (event.key === '?' && !isTyping && !event.altKey && !event.ctrlKey && !event.metaKey) {
            event.preventDefault();
            this.toggleHelpModal();

            return;
        }

        // Alt + H (Fallback)
        if (event.altKey && this.isShortcut(event, 'h', 'KeyH')) {
            event.preventDefault();
            this.toggleHelpModal();

            return;
        }

        if (!event.altKey || isTyping) {
            return;
        }

        if (this.isShortcut(event, 't', 'KeyT')) {
            event.preventDefault();
            this.focusElementById('topBookRelated');
        } else if (this.isShortcut(event, 'l', 'KeyL')) {
            event.preventDefault();
            this.focusElementById('bookviewerSidebar');
        } else if (this.isShortcut(event, 'r', 'KeyR')) {
            event.preventDefault();
            this.focusElementById('toolBarWrapper');
        } else if (this.isShortcut(event, 'v', 'KeyV')) {
            event.preventDefault();
            this.focusElementById('topViewControls');
        } else if (this.isShortcut(event, 'm', 'KeyM')) {
            event.preventDefault();
            this.focusElementById('pageContentWrapper');
        } else if (this.isShortcut(event, 'b', 'KeyB')) {
            event.preventDefault();
            this.focusElementById('footerWrapper');
        } else if (this.isShortcut(event, 'p', 'KeyP')) {
            event.preventDefault();
            const promise = this.chapterService.getPreviousChapter();

            promise.then((chapter)=> {
                const pageChangedEvent = new PageChangedEvent;
                pageChangedEvent.moveToPage = chapter.index;
                this.communicationService.announcePageTurn(pageChangedEvent);
            });
        } else if (this.isShortcut(event, 'n', 'KeyN')) {
            event.preventDefault();
            const promise = this.chapterService.getNextChapter();

            promise.then((chapter)=> {
                const pageChangedEvent = new PageChangedEvent;
                pageChangedEvent.moveToPage = chapter.index;
                this.communicationService.announcePageTurn(pageChangedEvent);
            });
        } else if (this.isShortcut(event, 'o', 'KeyO')) {
            event.preventDefault();
            this.sideBarService.triggerSidebarChangeEvent(new SidebarChangedEvent(true, ChapterContentComponent));
        } else if (this.isShortcut(event, 'q', 'KeyQ')) {
            event.preventDefault();
            this.sideBarService.triggerSidebarChangeEvent(new SidebarChangedEvent(true, SearchComponent));
        }
    }

    private isShortcut(event: KeyboardEvent, char: string, code: string): boolean {
        if (event.code === code) {
            return true;
        }

        const key = event.key.toLowerCase();
        const macOptionMap: Record<string, Array<string>> = {
            b: ['b', '√'],
            h: ['h', 'ª'],
            l: ['l', '@'],
            m: ['m', 'µ'],
            n: ['n', '~'],
            o: ['o', 'ø'],
            p: ['p', 'π'],
            q: ['q', '«'],
            r: ['r', '®'],
            t: ['t', '†'],
            v: ['v', '√']
        };

        const allowedKeys = macOptionMap[char.toLowerCase()] || [char.toLowerCase()];

        return allowedKeys.includes(key);
    }

    private checkIsTyping(element: HTMLElement | null): boolean {
        if (!element) {
            return false;
        }

        const tagName = element.tagName.toUpperCase();
        if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
            return true;
        }

        return element.hasAttribute('contenteditable') || element.closest('[contenteditable]') !== null;
    }

    toggleHelpModal(): void {
        if (this.isModalOpen) {
            this.modalService.closeModal(AccessibilityModalComponent.MODAL_ID);
            this.isModalOpen = false;

            return;
        }

        this.isModalOpen = true;
        this.modalService.openModal(AccessibilityModalComponent);
    }

    focusElementById(id: string): void {
        const element = document.getElementById(id);
        if (!element) {
            return;
        }

        this.focusElement(element);
    }

    focusElement(element: HTMLElement): void {
        if (element.hasAttribute('bookviewerAccessbilityComponent')) {
            element.focus();

            return;
        }

        const focusable = element.querySelector('a, button, input, select, textarea, [tabindex]') as HTMLElement;
        if (focusable) {
            focusable.focus();
        } else {
            element.setAttribute('tabindex', '-1');
            element.focus();
        }
    }

    private focusInsideModal(modal: HTMLElement): void {
        const focusable = modal.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;

        if (focusable) {
            focusable.focus();

            return;
        }

        if (!modal.hasAttribute('tabindex')) {
            modal.setAttribute('tabindex', '-1');
        }
        modal.focus();
    }

    private focusSidebar(): void {
        setTimeout(() => {
            const sidebarWrapper = document.getElementById('bookviewerExpandedSidebarContainer');

            if (sidebarWrapper) {
                if (!sidebarWrapper.hasAttribute('tabindex')) {
                    sidebarWrapper.setAttribute('tabindex', '-1');
                }

                sidebarWrapper.focus();
            }
        }, 100);
    }

    private returnFocusToPageContent(): void {
        const pageContentWrapper = document.getElementById('pageContentWrapper');
        if (pageContentWrapper && document.activeElement && pageContentWrapper.contains(document.activeElement)) {
            this.focusElementById('pageContentWrapper');
        }
    }
}
